import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Autocomplete, TextField, Checkbox } from '@mui/material';
import { ArrowLeft as ArrowLeftIcon } from '@mui/icons-material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { useGetUsersQuery, useUpdateUserMutation } from '../../../features/users/usersApiSlice';
import { useGetofficesQuery, useUpdateofficeMutation } from '../../../features/offices/officesApiSlice';
import { useLazyGetFilteredAssetRequestsQuery, useUpdateassetReqMutation } from '../../../features/assetrequest/assetReqApiSlice';
import { useLazyGetFilteredConsumableRequestsQuery, useUpdateConsumableReqMutation } from '../../../features/consumablerequest/consumableReqApiSlice';
import { useLazyGetFilteredComplaintsLazyQuery, useUpdateComplaintsMutation } from '../../../features/complaints/complaintsApiSlice';
import { CommonButton } from '../../../Styles/CommonStyles';
import useAuth from '../../../features/hooks/useAuth';
import FilteredAssetsFetcher from '../../Assets/Myasset/FilteredAssetsFetcher';
import { useRefreshMutation } from '../../../features/auth/authApiSlice';
import terminateUserRequests from '../terminateUserRequests';

// Icons for checkboxes
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Styled Components
const MainHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 10px 0 5px 20px;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #5C5891;
`;

const HeadingField = styled.div`
  font-weight: bold;
  margin-bottom: 20px;
`;

const SubHeadingField = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 10px 0 5px 5px;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #5C5891;
`;

const BackArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: 20px;
  cursor: pointer;
`;

const AddUser = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 20px;
`;

const AddUserFormContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 20px;
`;

const LabelInputWrapper = styled.div`
  display: ${(props) => props.disp};
  min-height: 80px;
  margin-right: 40px;
`;

const SubmitButton = styled(CommonButton)`
  height: 40px;
  margin: 0;
  background-color: #5C5891;
`;

const RelieveOfficeReq = ({userData, compToggle, setCompToggle}) =>{

    const Auth = useAuth();

    const [updateUser] = useUpdateUserMutation();
    const [updateOffice] = useUpdateofficeMutation();
    const [refresh] = useRefreshMutation();

    // RTK Query hooks for request termination
    const [getFilteredAssetRequests] = useLazyGetFilteredAssetRequestsQuery();
    const [updateAssetReq] = useUpdateassetReqMutation();
    const [getFilteredConsumableRequests] = useLazyGetFilteredConsumableRequestsQuery();
    const [updateConsumableReq] = useUpdateConsumableReqMutation();
    const [getFilteredComplaints] = useLazyGetFilteredComplaintsLazyQuery();
    const [updateComplaints] = useUpdateComplaintsMutation();

    const userRef = useRef();
    const count = useRef(0);
    const [allData, setAllData] = useState([]);
    const [filters, setFilters] = useState({
      perm_cur_store: [],
      perm_alloc_ofc: [],
      perm_alloc_user: [],
      perm_ids: [],
      perm_cur_loc: [],
      perm_name: [],
    });

    const [relieveOffices, setRelieveOffice] =useState([]);

    useEffect(() => {
      count.current = count.current + 1;
    });

    useEffect(() => {
        userRef?.current?.focus();
    }, [])
    // Set filters when ID changes
    useEffect(() => {
      if (Auth?.id) {
        setFilters({
          perm_alloc_ofc: [],
          perm_cur_loc: [],
          perm_cur_store: [],
          perm_alloc_user: [Auth?.id],
          perm_ids: [],
          perm_name: [],
        });
      } else {
        setFilters(null);
      }
    }, [Auth?.id]);

    const {officedata} = useGetofficesQuery("officeList", {
      selectFromResult: ({ data }) => ({
        officedata: data?.entities
      }),
    })    
  
    const {userdata} = useGetUsersQuery("usersList", {
      selectFromResult: ({ data }) => ({
        userdata: data?.entities
      }),
    })
    
    const onChangeuserform = (event, newValue, name) => {
      if (name !== "relieveOffices") return;

      const currentUser = userData[Auth.id];

      //We have to check here if the relieved office
      for (let office of newValue) {
        if (allData?.assets?.filter( x => (x.alloc_ofc === office) && (x.cur_loc === "user"))?.length) {
          return alert(`Assets have not been handed over from ${office} office`);
        }
        // Here we have to add the condition to check if any assets in that office are of BO asset type. Filtering algo for getting assets may be required.
        if (allData?.assets?.filter( x => (x.alloc_ofc === office) && (x.cur_loc === "office"))?.length) {
          return alert(`BO Assets have not been handed over from ${office} office`);
        }
        if (currentUser?.roles?.[office]?.includes("ItemAdder_A")) {
          return alert(`Store Role has not been handed over from ${office} office`);
        }     
      }
      setRelieveOffice(newValue);
    }
    
    const onClickFormSubmit = async (e) => {
      e.preventDefault();
      
      const currentUser = structuredClone(userData[Auth.id]);
      const { office, designation, roles, numSuffix, assets, bo_assets, store_assets } = currentUser;
    
      const notSelectedOffices = [office.mainCharge, ...(office.additionalCharge ?? [])]
        .filter(Boolean)
        .filter(x => !relieveOffices.includes(x));
    
      if (relieveOffices.includes(office.mainCharge) && notSelectedOffices.length) {
        return alert("If Main Office is to be relieved, all additional offices also need to be included in relieving request.");
      }
    
      const confirmMessage = `Are you sure you want to get relieved from ${relieveOffices.join(", ")} Office(s)? You will not be able to access any rights related to the office. For rejoining, a new request will have to be approved by the Reporting Officer.`;
    
      if (!window.confirm(confirmMessage)) return;
      console.log("Relieving from offices:", relieveOffices);

      // Terminate pending requests for offices the user is leaving
      try {
        console.log(`Terminating pending requests for user ${Auth.id} in offices: ${relieveOffices.join(', ')}`);
        
        // Show alert about request termination
        alert(`You are being relieved from office(s): ${relieveOffices.join(', ')}. All your pending requests will be automatically terminated.`);
        
        for (const office of relieveOffices) {
          console.log(`Terminating requests for user ${Auth.id} in office ${office}`);
          const hooks = {
            getFilteredAssetRequests,
            updateAssetReq,
            getFilteredConsumableRequests,
            updateConsumableReq,
            getFilteredComplaints,
            updateComplaints
          };
          const terminationResults = await terminateUserRequests(Auth.id, office, hooks);
          
          if (terminationResults.totalTerminated > 0) {
            console.log(`Successfully terminated ${terminationResults.totalTerminated} requests for office ${office}:`, terminationResults);
          }
          
          if (terminationResults.totalErrors > 0) {
            console.warn(`Encountered ${terminationResults.totalErrors} errors while terminating requests for office ${office}:`, terminationResults);
          }
        }
        
        // Show success message after termination
        alert(`Request termination completed. You have been successfully relieved from office(s): ${relieveOffices.join(', ')}`);
      } catch (error) {
        console.error('Error terminating user requests:', error);
        // Continue with office relieving even if termination fails
        alert(`Warning: Some pending requests could not be terminated automatically. Please contact administrator. Error: ${error.message}`);
      }
    
      try {
        // Process each relieved office
        await Promise.all(
          relieveOffices?.map(async (val) => {
            const tempOffice = Object.values(officedata)?.find((x) => x.name === val);
            
            // Update office data only if tempOffice exists
            if (tempOffice) {
              let { _id, ...updatedOffice } = structuredClone(tempOffice);
      
              if (roles[val]?.includes("UnitHead")) updatedOffice.unitHead = "";
              if (roles[val]?.includes("Bo")) updatedOffice.Bo = "";
              console.log(updatedOffice)
      
              await updateOffice(updatedOffice);
            }
    
            // Always remove user roles and related office details regardless of tempOffice existence
            if (office?.mainCharge === val) currentUser.office.mainCharge = "";
            currentUser.office.additionalCharge = (office.additionalCharge ?? []).filter((x) => x !== val);
    
            delete designation?.[val];
            delete numSuffix?.[val];
            delete assets?.[val];
            delete bo_assets?.[val];
            delete store_assets?.[val];
            delete roles?.[val];
          })
        );
        // Update user details after relieving
        let { _id, password, ...updatedUser } = currentUser;
        console.log(updatedUser)

        const resUser = await updateUser(updatedUser);
        
        console.log(resUser);
        setRelieveOffice([]);
        
        // Refresh JWT token to update office information in topbar
        try {
          await refresh();
        } catch (refreshError) {
          console.error("Error refreshing token:", refreshError);
        }
        
        // Show success message after relieving is complete
        alert(`Successfully relieved from ${relieveOffices.join(", ")} Office(s). You have been removed from all associated roles and responsibilities.`);
        
      } catch (error) {
        console.error("Error processing request:", error);
        alert("Something went wrong. Please try again.");
      }
    };    

    const onClickBackArrow = (e) => {
      let tempToggle = structuredClone(compToggle);
          tempToggle.home = true;
          tempToggle.relieveOff = false;
          setCompToggle(tempToggle);
    }  
  
return (
  <>
    <FilteredAssetsFetcher filters={filters} onDataFetched={setAllData} />
  
    {/* Back Navigation */}
    <BackArrowContainer onClick={onClickBackArrow}>
      <ArrowLeftIcon />
      <span>Back to Change Office Req Page</span>
    </BackArrowContainer>

    {/* Heading */}
    <MainHeading>
      <HeadingField>Relieve from Office</HeadingField>
    </MainHeading>

    {/* Form Submission */}
    <form onSubmit={onClickFormSubmit}>
      <AddUserFormContainer>
        {/* Email Field */}
        <LabelInputWrapper>
          <TextField
            id="email-field"
            size="small"
            ref={userRef}
            name="email"
            label="Email"
            value={Auth.email}
            variant="outlined"
            InputProps={{ readOnly: true }}
          />
        </LabelInputWrapper>

        {/* Present Main Office */}
        <LabelInputWrapper>
          <TextField
            id="main-office-field"
            size="small"
            name="mainOffice"
            label="Present Main Office"
            value={
              Object.values(userdata)?.find((x) => x.email === Auth.email)?.office
                ?.mainCharge.length
                ? Object.values(userdata)?.find((x) => x.email === Auth.email)
                    ?.office.mainCharge
                : "Vacant"
            }
            variant="outlined"
            InputProps={{ readOnly: true }}
          />
        </LabelInputWrapper>

        {/* Present Additional Charge Offices */}
        <LabelInputWrapper>
          <TextField
            id="additional-charge-field"
            size="small"
            name="additionalCharge"
            label="Present Additional Charge Offices"
            value={
              Object.values(userdata)
                ?.find((x) => x.email === Auth.email)
                ?.office.additionalCharge.length
                ? Object.values(userdata)
                    ?.find((x) => x.email === Auth.email)
                    ?.office.additionalCharge.join(", ")
                : "No Additional Charge"
            }
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{ width: "150%" }}
          />
        </LabelInputWrapper>
      </AddUserFormContainer>

      {/* Office Selection */}
      <AddUser>
        <SubHeadingField>
          Select Office/Offices to generate relieving request.
        </SubHeadingField>
      </AddUser>

      <AddUserFormContainer>
        <LabelInputWrapper>
          <Autocomplete
            multiple
            id="office-selection"
            size="small"
            onChange={(event, newValue) =>
              onChangeuserform(event, newValue, "relieveOffices")
            }
            value={relieveOffices}
            options={[
              userData[Auth.id]?.office?.mainCharge,
              ...userData[Auth.id]?.office?.additionalCharge,
            ].filter((x) => x !== "")}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select Offices" placeholder="Offices" />
            )}
            sx={{ minWidth: "250px", backgroundColor: "white" }}
          />
        </LabelInputWrapper>
      </AddUserFormContainer>

      {/* Submit Button */}
      <SubmitButton type="submit">Submit</SubmitButton>
    </form>
  </>

      )
}
export default RelieveOfficeReq;

